import { useState } from "react";
import { Sparkle, FileDoc, FilePdf, FilePpt, FileXls, EnvelopeSimple, WhatsappLogo, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * One-click "Generate McKinsey Narrative" button + Email/WhatsApp share.
 * Props:
 *   sourceType: 'case' | 'sighting' | 'investigation' | 'finance' | 'hr' | 'bd'
 *   sourceId:   string
 *   path:       url path prefix — e.g. `/cases/${case_id}/mckinsey/narrative`
 *               or `/finance/clients/${client_id}/mckinsey/narrative`
 *   label:      optional button text
 */
export default function McKinseyButton({ sourceType = "case", sourceId, path, label = "McKinsey Narrative" }) {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState("internal");
  const [busy, setBusy] = useState(null);
  const [shareOpen, setShareOpen] = useState(null); // "email" | "whatsapp" | null
  const [lastArtifact, setLastArtifact] = useState(null);

  const download = async (fmt) => {
    if (!sourceId && !path) return;
    setBusy(fmt);
    try {
      const url = path || `/cases/${sourceId}/mckinsey/narrative`;
      const resp = await api.get(`${url}.${fmt}?audience=${audience}`, { responseType: "blob" });
      const blob = new Blob([resp.data]);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${sourceType}_${sourceId || "report"}_${audience}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      setLastArtifact({ fmt, name: a.download });
    } catch (e) {
      alert("Failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  return (
    <div className="relative inline-block" data-testid={`mckinsey-button-${sourceType}`}>
      <button
        data-testid={`mckinsey-trigger-${sourceType}`}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[var(--brand-primary,#263776)] to-[var(--brand-primary-d,#3d52a5)] text-white text-xs font-semibold rounded shadow hover:opacity-90 transition">
        <Sparkle weight="fill" size={14} className="text-[var(--brand-accent,#F47D20)]" />
        {label}
      </button>
      {open && (
        <div data-testid={`mckinsey-panel-${sourceType}`} className="absolute right-0 mt-2 w-80 bg-[var(--surface,#fff)] border border-[var(--border)] rounded-lg shadow-lg z-30 p-4 space-y-3">
          <div className="text-sm font-bold text-[var(--brand-primary,#263776)]">Generate McKinsey Narrative</div>
          <div className="text-xs text-[var(--text-muted)]">SCQA structure · Claude Sonnet 4.5 · 30-min cached</div>
          <div className="bg-[var(--surface-2)] rounded p-2">
            <label className="block text-[11px] text-[var(--text-muted)] mb-1.5">Audience</label>
            <div className="flex gap-1">
              {[["internal", "Internal · full detail"], ["client", "Client · jargon-free"]].map(([k, lbl]) => (
                <button
                  key={k}
                  data-testid={`mckinsey-audience-${k}`}
                  onClick={() => setAudience(k)}
                  className={`flex-1 px-2 py-1 text-[11px] rounded transition ${audience === k ? "bg-[var(--brand-primary,#263776)] text-white" : "bg-[var(--surface)] text-[var(--text-secondary)] hover:bg-[var(--surface-3)]"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              ["docx", FileDoc, "Word"],
              ["pdf", FilePdf, "PDF"],
              ["pptx", FilePpt, "PowerPoint"],
              ["xlsx", FileXls, "Excel"],
            ].map(([fmt, Icon, name]) => (
              <button
                key={fmt}
                data-testid={`mckinsey-download-${fmt}`}
                disabled={busy !== null}
                onClick={() => download(fmt)}
                className="flex items-center gap-2 px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-xs hover:border-[var(--brand-primary,#263776)] hover:bg-[var(--surface-2)] disabled:opacity-50">
                <Icon weight="fill" size={20} className="text-[var(--brand-primary,#263776)]" />
                <span className="flex-1 text-left">{name}</span>
                {busy === fmt && <span className="text-[var(--text-muted)]">…</span>}
              </button>
            ))}
          </div>
          <div className="border-t border-[var(--border)] pt-2 grid grid-cols-2 gap-2">
            <button
              data-testid={`mckinsey-share-email`}
              onClick={() => setShareOpen("email")}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-xs rounded transition">
              <EnvelopeSimple size={14} /> Share via Email
            </button>
            <button
              data-testid={`mckinsey-share-whatsapp`}
              onClick={() => setShareOpen("whatsapp")}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#128C7E] text-xs rounded transition">
              <WhatsappLogo size={14} weight="fill" /> Share via WhatsApp
            </button>
          </div>
          <button onClick={() => setOpen(false)} className="text-[11px] text-[var(--text-faint)] hover:text-[var(--text-muted)]">close</button>
        </div>
      )}
      {shareOpen && (
        <ShareModal
          mode={shareOpen}
          sourceType={sourceType}
          sourceId={sourceId}
          lastArtifact={lastArtifact}
          onClose={() => setShareOpen(null)}
        />
      )}
    </div>
  );
}

function ShareModal({ mode, sourceType, sourceId, lastArtifact, onClose }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(`SPAN McKinsey Narrative — ${sourceType} ${sourceId || ""}`.trim());
  const [body, setBody] = useState(`Please find the SPAN CasePrime McKinsey-grade narrative for ${sourceType} ${sourceId || ""}.\n\nGenerated by SPAN CasePrime · Claude Sonnet 4.5`);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const send = async () => {
    const recipients = to.split(/[,\s;]+/).map(s => s.trim()).filter(Boolean);
    if (!recipients.length) { alert("Add at least one recipient"); return; }
    setBusy(true);
    try {
      if (mode === "email") {
        const html = `<p>${body.replace(/\n/g, "<br/>")}</p>`;
        const { data } = await api.post("/share/email", {
          to: recipients,
          subject,
          body_html: html,
        });
        setDone({ ok: true, count: data.sent, skipped: data.skipped_suppressed || 0 });
      } else {
        // WhatsApp via wa.me deep link (no Twilio needed)
        const text = encodeURIComponent(body);
        recipients.forEach((num, i) => {
          const clean = num.replace(/[^0-9+]/g, "").replace(/^\+/, "");
          const url = `https://wa.me/${clean}?text=${text}`;
          setTimeout(() => window.open(url, "_blank"), i * 200);
        });
        // Also log to backend (queued) for audit
        try { await api.post("/share/whatsapp", { to: recipients, body }); } catch (_e) { /* best-effort log */ }
        setDone({ ok: true, count: recipients.length, skipped: 0, whatsapp: true });
      }
    } catch (e) {
      alert("Failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid={`share-modal-${mode}`}>
      <div className="bg-[var(--surface)] rounded-lg shadow-xl w-full max-w-[520px] p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold text-[var(--text-primary)]">
            {mode === "email" ? "Share narrative via Email" : "Share narrative via WhatsApp"}
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {done ? (
          <div className="space-y-3">
            <div className="text-sm text-[var(--status-success)] font-semibold">
              {done.whatsapp ? `Opened ${done.count} WhatsApp tab(s) — paste & send manually.` : `Sent to ${done.count} recipient(s).${done.skipped ? ` ${done.skipped} suppressed.` : ""}`}
            </div>
            <div className="text-xs text-[var(--text-muted)]">
              {!done.whatsapp && "Email delivered via Resend from admin@spancaseprime.com."}
            </div>
            <div className="flex justify-end"><button onClick={onClose} className="span-btn">Done</button></div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-1">
                {mode === "email" ? "Recipients (comma-separated emails)" : "WhatsApp numbers (E.164, e.g. +919876543210)"}
              </label>
              <input
                data-testid={`share-${mode}-to`}
                value={to}
                onChange={e => setTo(e.target.value)}
                placeholder={mode === "email" ? "client@example.com, ceo@example.com" : "+919876543210, +447911123456"}
                className="span-input w-full"
              />
            </div>
            {mode === "email" && (
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Subject</label>
                <input data-testid="share-email-subject" value={subject} onChange={e => setSubject(e.target.value)} className="span-input w-full" />
              </div>
            )}
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Message</label>
              <textarea data-testid={`share-${mode}-body`} value={body} onChange={e => setBody(e.target.value)} rows={5} className="span-input w-full font-mono text-[12px]" />
            </div>
            {lastArtifact && (
              <div className="text-[11px] text-[var(--text-muted)] bg-[var(--surface-2)] px-2 py-1.5 rounded">
                Tip: Last download was <span className="font-mono">{lastArtifact.name}</span>. {mode === "whatsapp" ? "Attach it manually after WhatsApp opens." : "Attach via your email composer if needed (backend share endpoint sends a link)."}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
              <button data-testid={`share-${mode}-send`} disabled={busy} onClick={send} className="span-btn disabled:opacity-50">
                {busy ? "Sending…" : mode === "email" ? "Send email" : "Open WhatsApp"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
